'use strict';

const libLocationWorkspaceProvider = require('../providers/LocationWorkspace-Provider.js');

const isView = (pProv) => pProv.mode === 'view';
const hasQRCode = (pProv) => isView(pProv) && !!pProv.record.QRCode;

module.exports =
{
	Identifier: 'LocationWorkspace',
	Ordinal: 259,
	ProviderClass: libLocationWorkspaceProvider,
	Entity: { Name: 'Location', IDField: 'IDLocation', Collection: 'Locations', GUIDField: 'GUIDLocation' },
	Header:
	{
		Eyebrow: 'Location',
		Title: (pRec) => pRec.Name || `Location ${pRec.IDLocation}`,
		Subtitle: (pRec, pProv) => pProv.headerSubtitle(),
		// The location's QR code in the header corner, as the Sample page shows a sample's.
		Extra: (pProv) => pProv.headerExtra(),
		AfterRender: (pProv) => pProv.headerAfterRender(),
		Actions:
		[
			{ Label: 'Edit', Icon: 'fas fa-pen', Style: 'is-link', OnClick: 'goToEdit()', When: (pProv) => isView(pProv) && pProv.canUpdate() },
			{ Label: 'Print', Icon: 'fas fa-print', Style: 'is-light', OnClick: 'printLabel()', When: hasQRCode },
			{ Label: 'Print options…', Style: 'is-light', OnClick: 'printLabel(true)', When: hasQRCode },
			{ Label: '', Icon: 'fas fa-trash', Style: 'is-danger', OnClick: 'deleteLocation()', When: (pProv) => isView(pProv) && pProv.canDelete() },
		],
		ListRoute: '#/PSRS/Location/List',
	},
	Tabs:
	[
		{ Key: 'overview', Label: 'Details', Icon: 'fas fa-circle-info', Content: { kind: 'overview' } },
		{ Key: 'samples', Label: 'Samples', Icon: 'fas fa-vial', Content: { kind: 'render', ref: 'renderSamples' }, OnEnter: (pProv) => pProv.ensureSamplesAsync() },
	],
	FieldGroups:
	[
		{
			Name: 'Location',
			Fields:
			[
				{ Key: 'Name', Label: 'Name', Wide: true },
				{ Key: 'Type', Label: 'Type' },
				{ Key: 'QRCode', Label: 'QR Code', Mono: true },
				{ Key: 'ParentIDLocation', Label: 'Inside', Card: 'Location' },
				{ Key: 'Description', Label: 'Description', Wide: true },
			],
		},
		{
			Name: 'Photo',
			Fields:
			[
				{ Key: 'IDObservation', Label: 'Photo', Wide: true, Value: (pRec, pProv) => pProv.photoHTML() },
			],
		},
		{
			Name: 'Map Location',
			Fields:
			[
				{ Key: 'Latitude', Label: 'Latitude', Mono: true },
				{ Key: 'Longitude', Label: 'Longitude', Mono: true },
				{ Key: 'Map', Label: 'Map', Wide: true, Value: (pRec, pProv) => pProv.mapHTML() },
			],
		},
		{
			Name: 'Address',
			Fields:
			[
				{ Key: 'Address', Label: 'Street', Wide: true },
				{ Key: 'City', Label: 'City' },
				{ Key: 'State', Label: 'State' },
				{ Key: 'Zip', Label: 'Zip' },
				{ Key: 'Phone', Label: 'Phone' },
			],
		},
	],
	CSSExtra: /*css*/`
		.locw-subline { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; margin-top: 0.35rem; font-size: 0.82rem; color: var(--theme-color-text-muted, #8a94a3); }
		.locw-type { font-size: 0.7rem; font-weight: 700; padding: 0.12rem 0.55rem; border-radius: 999px; background: var(--theme-color-background-selected, #eef1f8); color: var(--theme-color-brand-primary, #5650e6); }
		.locw-crumbs a { color: var(--theme-color-text-secondary, #45505f); }
		.locw-crumbs a:hover { color: var(--theme-color-brand-primary, #5650e6); }
		.locw-sep { opacity: 0.6; }
		.locw-inside { margin-bottom: 0.75rem; font-size: 0.9rem; }
		.locw-count { margin-bottom: 0.6rem; font-size: 0.86rem; color: var(--theme-color-text-muted, #6b7686); }
		.locw-photo-link { display: inline-block; }
		.locw-photo { display: block; max-width: 100%; max-height: 280px; border-radius: 6px; }
		.locw-coords { font-size: 0.86rem; margin-bottom: 0.45rem; font-variant-numeric: tabular-nums; }
		.locw-map { height: 300px; border-radius: 8px; border: 1px solid var(--theme-color-border-light, #e8ebf0); z-index: 0; }
		.locw-qr-thumb { flex: 0 0 auto; width: 80px; height: 80px; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 6px; padding: 3px; background: #fff; margin-left: 0.4rem; }
		.locw-qr-thumb canvas, .locw-qr-thumb img, .locw-qr-thumb svg { width: 100%; height: 100%; display: block; }
		.locw-toolbar { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
		.locw-cart-bar { align-items: center; justify-content: space-between; }
		.locw-cart { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
		.locw-cart .hl-scan {
			--hl-scan-background: var(--theme-color-background-panel, var(--theme-color-background-primary, #fff));
			--hl-scan-text: var(--theme-color-text-secondary, #45505f);
			--hl-scan-text-strong: var(--theme-color-text-primary, #1f2733);
			--hl-scan-text-weak: var(--theme-color-text-muted, #8a94a3);
			--hl-scan-border: var(--theme-color-border-light, #e8ebf0);
			--hl-scan-border-weak: var(--theme-color-border-light, #eef1f5);
			--hl-scan-head-background: var(--theme-color-background-tertiary, #f4f6f9);
			--hl-scan-input-background: var(--theme-color-background-primary, #fff);
			--hl-scan-input-text: var(--theme-color-text-primary, #1f2733);
			--hl-scan-input-border: var(--theme-color-border-default, #d7dce3);
		}
		.locw-error { font-size: 0.88rem; color: var(--theme-color-status-error, #b3382f); background: var(--theme-color-status-error-bg, #fbeaea); border-radius: 6px; padding: 0.5rem 0.7rem; }
	`,
};
